import App from '@/App';
import { PrivateRoute } from '@/components/private-route/private-route';
import { PublicRoute } from '@/components/public-route/public-route';
import { OnboardingRoute } from '@/components/onboarding-route/onboarding-route';
import { RoleBasedRoute } from '@/components/role-based-route/role-based-route';
import { PlanTier, UserRole } from '@/services/user-service/user.service.types';

import { Dashboard } from '@/pages/dashboard/dashboard';
import { ResetPassword } from '@/pages/reset-password/reset-password';
import { ForgotPassword } from '@/pages/forgot-password/forgot-password';
import { SignIn } from '@/pages/sign-in/sign-in';
import { SignUp } from '@/pages/sign-up/sign-up';
import { SelectPlan } from '@/pages/onboarding/select-plan/select-plan';
import { Checkout } from '@/pages/onboarding/checkout/checkout';
import { Success } from '@/pages/onboarding/success/success';
import { Navigate, RouteObject } from 'react-router';
import { Surveys } from '@/pages/surveys/surveys';
import { Maintenances } from '@/pages/maintenances/maintenances';
import { Documents } from '@/pages/documents/documents';
import { DocumentDetails } from '@/pages/documents/document-details/document-details';
import { CreateDocumentPage } from '@/pages/documents/create-document/create-document';
import { Profile } from '@/pages/profile/profile';
import { SupportPage } from '@/pages/support/support';
import { NotFound } from '@/pages/not-found/not-found';
import { Conversations } from '@/pages/conversations/conversations';
import { Company } from '@/pages/company/company';
import { VerifySupportTicket } from '@/pages/verify-support-ticket/verify-support-ticket';
import { PublicSupportPage } from '@/pages/public-support/public-support';
import { PhoneNumbers } from '@/pages/phone-numbers/phone-numbers';
import { Voices } from '@/pages/voices/voices';
import { Agents } from '@/pages/agents/agents';
import { CreateAgent } from '@/pages/agents/create-agent/create-agent';
import { AgentDetail } from '@/pages/agents/agent-detail/agent-detail';
import { Webchat } from '@/pages/webchat/webchat';
import { Statistics } from '@/pages/statistics/statistics';
import { Satisfaction } from '@/pages/satisfaction/satisfaction';
import ComponentsGallery from '@/pages/dev/components-gallery';
import { OutboundCalls } from '@/pages/outbound-calls/outbound-calls';
import { OUTBOUND_CALLS_ROUTES } from '@/pages/outbound-calls/outbound-calls.routes';
import {
  AccessGate,
  HIDDEN_SECTION_KEYS,
  PROFILE_SECTION,
  SectionKey,
  UNLISTED_SECTION_KEYS,
  VISIBLE_SECTIONS,
  accessGate,
} from '@/config/navigation';

type PrivateRouteDefinition = {
  key: SectionKey | (typeof HIDDEN_SECTION_KEYS)[number] | (typeof UNLISTED_SECTION_KEYS)[number];
  path: string;
  element: React.ComponentType;
  children?: RouteObject[];
  allowedRoles?: UserRole[];
  deniedRoles?: UserRole[];
  allowedPlanTiers?: PlanTier[];
  requireConversationalAgent?: boolean;
};

/**
 * A visible section's gate is read from `@/config/navigation`, never from the table below,
 * which is what stops the menu and the router disagreeing about who may reach a screen.
 * Applying it per route by hand is the same thing one entry at a time, and misses one.
 *
 * Hidden and unlisted keys have no menu entry to drift from, so they declare their own.
 * A visible section that declares one here is a mistake rather than an override, and it
 * throws at module eval: a silently dropped gate leaves the route open.
 */
const gateFor = (definition: PrivateRouteDefinition): AccessGate => {
  const own = accessGate(definition);
  const section = VISIBLE_SECTIONS.find((candidate) => candidate.key === definition.key);
  if (!section) return own;

  if (Object.values(own).some(Boolean)) {
    throw new Error(
      `Section "${definition.key}" declares its gate in @/config/navigation, drop the inline one`
    );
  }
  return accessGate(section);
};

const privateRoutesDefinition: PrivateRouteDefinition[] = [
  { key: 'dashboard', path: '/', element: Dashboard },
  {
    key: 'conversations',
    path: 'conversations',
    element: Conversations,
    // The detail view is a drawer owned by the list page, so this child renders nothing.
    // It exists so /conversations/:callId stays a real deep link.
    children: [{ path: ':callId', element: null }],
  },
  {
    key: 'outbound-calls',
    path: 'outbound-calls',
    element: OutboundCalls,
    children: OUTBOUND_CALLS_ROUTES,
  },
  { key: 'statistics', path: 'statistics', element: Statistics },
  { key: 'satisfaction', path: 'satisfaction', element: Satisfaction },
  {
    // Command Center is multi-channel (legacy RAI voice + SMS + AI calls + webchat).
    // We do NOT gate on `requireConversationalAgent` here — rai-only companies
    // (whose users have `conversational_agent: false`) still need access to
    // monitor their live voice calls and SMS.
    key: 'command_center',
    path: 'command_center',
    element: Webchat,
    allowedRoles: ['admin', 'company_admin'],
  },
  {
    key: 'surveys',
    path: 'surveys',
    element: Surveys,
    deniedRoles: ['reviewer'],
    allowedPlanTiers: ['Custom'],
  },
  {
    key: 'work-orders',
    path: 'work-orders',
    element: Maintenances,
    deniedRoles: ['reviewer'],
    allowedPlanTiers: ['Custom'],
  },
  {
    key: 'documents',
    path: 'documents',
    element: Documents,
    children: [
      { path: 'new', element: <CreateDocumentPage /> },
      { path: ':documentId', element: <DocumentDetails /> },
    ],
  },
  {
    key: 'phone-numbers',
    path: 'phone-numbers',
    element: PhoneNumbers,
    allowedRoles: ['admin'],
    requireConversationalAgent: true,
  },
  {
    key: 'voices',
    path: 'voices',
    element: Voices,
    allowedRoles: ['admin'],
    requireConversationalAgent: true,
  },
  {
    key: 'agents',
    path: 'agents',
    element: Agents,
    allowedRoles: ['admin'],
    requireConversationalAgent: true,
    children: [
      { path: 'new', element: <CreateAgent /> },
      { path: ':agentId', element: <AgentDetail /> },
    ],
  },
  { key: 'company', path: 'company', element: Company },
  {
    key: PROFILE_SECTION.key,
    path: 'profile',
    element: Profile,
    ...accessGate(PROFILE_SECTION),
  },
  { key: 'support', path: 'support', element: SupportPage },
];

const publicRoutesDefinition = [
  { path: 'sign-in', element: SignIn },
  { path: 'sign-up', element: SignUp },
  { path: 'reset-password', element: ResetPassword },
  { path: 'forgot-password', element: ForgotPassword },
];

// Widened from the `as const` tuple so `includes` accepts a plain string key.
const hiddenKeys: readonly string[] = HIDDEN_SECTION_KEYS;

// A hidden section is never built, so its url falls to the `*` catch-all and /404.
const privateRoutes = privateRoutesDefinition
  .filter(({ key }) => !hiddenKeys.includes(key))
  .map((definition) => {
    const { path, element: RouteComponent, children } = definition;
    const gate = gateFor(definition);

    return {
      path,
      element: (
        <PrivateRoute>
          {Object.values(gate).some(Boolean) ? (
            <RoleBasedRoute {...gate}>
              <RouteComponent />
            </RoleBasedRoute>
          ) : (
            <RouteComponent />
          )}
        </PrivateRoute>
      ),
      children,
    };
  });

const publicRoutes = publicRoutesDefinition.map(({ path, element: RouteComponent }) => ({
  path,
  element: (
    <PublicRoute>
      <RouteComponent />
    </PublicRoute>
  ),
}));

const onboardingRoutes: RouteObject[] = [
  {
    path: 'onboarding/plan',
    element: (
      <OnboardingRoute>
        <SelectPlan />
      </OnboardingRoute>
    ),
  },
  {
    path: 'onboarding/checkout',
    element: (
      <OnboardingRoute>
        <Checkout />
      </OnboardingRoute>
    ),
  },
  {
    path: 'onboarding/success',
    element: (
      <OnboardingRoute>
        <Success />
      </OnboardingRoute>
    ),
  },
];

// Dev-only visual catalogue of the component library. Comparing a literal lets
// Rollup drop both the route and the page from a production bundle. It carries
// no data, so it needs no session and stays outside PrivateRoute.
const galleryRoutes: RouteObject[] =
  import.meta.env.VITE_COMPONENT_GALLERY === 'true'
    ? [{ path: 'dev/components', element: <ComponentsGallery /> }]
    : [];

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <App />,
    errorElement: <Navigate to="/" />,
    children: [
      ...privateRoutes,
      ...publicRoutes,
      { path: 'verify/support-ticket/:token', element: <VerifySupportTicket /> },
      { path: 'support-ticket', element: <PublicSupportPage /> },
      ...onboardingRoutes,
      ...galleryRoutes,
      { path: '404', element: <NotFound /> },
      { path: '*', element: <Navigate to="/404" replace /> },
    ],
  },
];
